import React, { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@material-ui/core';
import { KeyboardArrowDown, KeyboardArrowUp } from '@material-ui/icons';
import moment from 'moment';
import {
  IPartnerRatingByPartner,
  IRealEstateRatingByPartner,
  IServiceType,
  ServiceTypeKind,
} from '../../../interfaces';
import { useGetRealEstateRatingByPartnerV2 } from '../../../hooks';
import { V2_TABLE_COLUMN_COUNT } from './constants';
import PartnerRatingColGroup from './PartnerRatingColGroup';
import PartnerRatingValueCells from './PartnerRatingValueCells';
import PartnerRatingMoDetailDialog from './PartnerRatingMoDetailDialog';
import PartnerRatingNpsDetailDialog from './PartnerRatingNpsDetailDialog';

interface IProps {
  year: number;
  serviceType?: IServiceType;
  // Определяет третью пару колонок, см. PartnerRatingValueCells
  serviceTypeKind?: ServiceTypeKind;
  partnerRating: IPartnerRatingByPartner;
  realEstateRating: IRealEstateRatingByPartner;
}

const PartnerRatingTableRow = ({ partnerRating, realEstateRating, serviceType, serviceTypeKind, year }: IProps) => {
  const [open, setOpen] = useState(false);
  // Окна детализации: данные хранятся отдельно от флага открытия — чтобы корректно отыграть анимацию закрытия.
  const [moDetail, setMoDetail] = useState<IRealEstateRatingByPartner | null>(null);
  const [moDetailOpen, setMoDetailOpen] = useState(false);
  const [npsDetail, setNpsDetail] = useState<IRealEstateRatingByPartner | null>(null);
  const [npsDetailOpen, setNpsDetailOpen] = useState(false);

  const { fetchRealEstateRatingByPartnerV2, data: monthsByKey, loading, error, success } =
    useGetRealEstateRatingByPartnerV2();

  const mapKey = Object.values({
    partnerGuid: partnerRating.partnerGuid,
    realEstateGuid: realEstateRating.realEstateGuid,
    serviceTypeId: serviceType?.serviceTypeId,
    year,
  })
    .filter(Boolean)
    .join('-');

  useEffect(() => {
    if (!open) return;
    fetchRealEstateRatingByPartnerV2(partnerRating.partnerGuid, realEstateRating.realEstateGuid, {
      params: { serviceTypeId: serviceType?.serviceTypeId, year },
    });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const months = monthsByKey.get(mapKey) || [];

  const handleOpenMoDetail = (month: IRealEstateRatingByPartner) => {
    setMoDetail(month);
    setMoDetailOpen(true);
  };

  const handleOpenNpsDetail = (month: IRealEstateRatingByPartner) => {
    setNpsDetail(month);
    setNpsDetailOpen(true);
  };

  const renderStatusRow = (content: React.ReactNode) => (
    <TableRow>
      <TableCell colSpan={V2_TABLE_COLUMN_COUNT} align="center" style={{ paddingTop: 24, paddingBottom: 24 }}>
        {content}
      </TableCell>
    </TableRow>
  );

  return (
    <>
      <TableRow hover style={{ cursor: 'pointer' }} onClick={() => setOpen(!open)}>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <b>{realEstateRating.realEstateName}</b>

            <IconButton aria-label="Развернуть строку" size="small">
              {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
          </Box>
        </TableCell>

        <PartnerRatingValueCells rating={realEstateRating} serviceTypeKind={serviceTypeKind} />
      </TableRow>

      <Box component={TableRow} sx={{ borderBottom: 'unset' }}>
        <TableCell colSpan={V2_TABLE_COLUMN_COUNT} style={{ padding: 0, border: 'none' }}>
          <Collapse in={open} unmountOnExit>
            <Table size="small" style={{ tableLayout: 'fixed' }}>
              <PartnerRatingColGroup />

              <TableBody>
                {loading
                  ? renderStatusRow(<CircularProgress size={24} />)
                  : Boolean(error) && !months.length
                  ? renderStatusRow(
                      <Typography component="span" variant="body2">
                        {error}
                      </Typography>,
                    )
                  : success && !months.length
                  ? renderStatusRow(
                      <Typography component="span" variant="body2">
                        Данные отсутствуют
                      </Typography>,
                    )
                  : months.map((month, index) => (
                      <TableRow key={index}>
                        <TableCell style={{ paddingLeft: 32 }}>
                          {moment(month.dateAt, 'YYYY-MM').format('MMMM YYYY')}
                        </TableCell>

                        <PartnerRatingValueCells
                          rating={month}
                          serviceTypeKind={serviceTypeKind}
                          onMoClick={() => handleOpenMoDetail(month)}
                          onNpsClick={() => handleOpenNpsDetail(month)}
                        />
                      </TableRow>
                    ))}
              </TableBody>
            </Table>
          </Collapse>
        </TableCell>
      </Box>

      {moDetail && (
        <PartnerRatingMoDetailDialog
          open={moDetailOpen}
          dateAt={moDetail.dateAt}
          realEstateName={moDetail.realEstateName}
          relationRating={moDetail.relationRating}
          criteriaRatings={moDetail.criteriaRatings}
          onClose={() => setMoDetailOpen(false)}
          TransitionProps={{ onExited: () => setMoDetail(null) }}
        />
      )}

      {npsDetail && (
        <PartnerRatingNpsDetailDialog
          open={npsDetailOpen}
          realEstateGuid={npsDetail.realEstateGuid}
          realEstateName={npsDetail.realEstateName}
          dateAt={npsDetail.dateAt}
          onClose={() => setNpsDetailOpen(false)}
          TransitionProps={{ onExited: () => setNpsDetail(null) }}
        />
      )}
    </>
  );
};

export default PartnerRatingTableRow;
